/**
 * A complete CS Scratchpad (header, contents, docs, and discussion)
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import classNames from "classnames";
import {compose} from "mu-lambda";
import * as React from "react";
import {findDOMNode} from "react-dom";

import {
    enums,
    withConversionContext,
    withMarkConversion,
    type PartialLearningTimeInfo,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import type {GqlFetchFn} from "@khan/data-gql";
import {standardTransitionMs} from "@khan/shared-styles";
import {scrollToElement} from "@khan/util-scrolling";

import ProgrammingDocs from "#content-library-legacy/modules/programming-documentation.tsx";
import withGql, {
    type WithGqlProps,
    type WithoutGql,
} from "#data-access/gql/with-gql.tsx";
import Discussion, {
    type StatefulDiscussionType,
} from "#discussion-package/components/stateful-discussion.tsx";
import type {DiscussionContext} from "#discussion-package/types.ts";
import $ from "#jquery/jquery-with-scratchpad-plugins.js";
import type {
    Scratchpad,
    UserScratchpad,
} from "#scratchpad-base-package/scratchpads.ts";
import type {ScratchpadChallenge} from "#scratchpad-challenge-package/scratchpad-challenge.ts";
import type {ProfileModelType} from "#shared-package/profile-model.ts";

import type {ScratchpadTopic} from "./internal/types.ts";
import ScratchpadAbout from "./scratchpad-about.tsx";
import ScratchpadContent from "./scratchpad-content.tsx";
import ScratchpadGuidelines from "./scratchpad-guidelines.tsx";
import ScratchpadHeader from "./scratchpad-header.tsx";
import ScratchpadSpinOffs from "./scratchpad-spinoffs.tsx";
import ScratchpadTabs, {type Tab} from "./scratchpad-tabs.tsx";
import ScratchpadTranscript from "./scratchpad-transcript.tsx";
import type {SaveProgramFn} from "./scratchpad-ui.tsx";
import * as ScratchpadUtils from "./scratchpad-utils.ts";
import type {DiscussionMeta, LiveEditor} from "./types.ts";

type DiscussionProjectFeedbackT =
    typeof import("../discussion-package/discussion-projectfeedback.tsx").default;

type ScratchpadDiscussionContext = DiscussionContext & {
    // id of the scratchpad, passed to <Discussion> as `contentId` prop
    focusId: string;
    // type of scratchpad, passed to <Discussion> as `contentKind` prop
    focusKind: "scratchpad" | "talkthrough" | "project" | "interactive";
    canEdit: boolean;
    isOwner?: boolean;
};

// These props should contain everything that's in `context` as defined in
// scratchpad-ui.js.
type OwnProps = {
    // The profile object of the person who made this program
    // Undefined for new programs and learnable content on local dev server
    creatorProfile?: ProfileModelType;
    // Options to be passed in to the discussion logic
    // Undefined for new programs, challenges
    discussion: ScratchpadDiscussionContext | null | undefined;
    challenge: typeof ScratchpadChallenge | null | undefined;
    // Is this scratchpad embedded in another page (either via an iframe
    // or as inside a tutorial)
    embedded: boolean;
    // Is this scratchpad being embedded inside a frame
    embeddedAndWithFrame?: boolean;
    // The current version of the app engine server
    appVersion: string;
    // Should the discussion be hidden
    hideDiscussion?: boolean;
    // Determine if the save button should be hidden
    hideSave: boolean;
    // Does this scratchpad have any audio associated with it
    hasAudio: boolean;
    // Should the code editor be hidden?
    hideEditor: boolean;
    // Can this scratchpad be saved?
    isSaveable: boolean;
    // Determine if a spin-off is currently allowed
    isSpinOffAllowed: () => boolean;
    showDescription: boolean;
    // Should the documentation be hidden
    hideDocumentation?: boolean;
    // Should the spin-offs be hidden
    hideSpinOffs?: boolean;
    hideEditTitle: boolean;
    hideDelete: boolean;
    hideVoteControls: boolean;
    hideVotes: boolean;
    // Are we in edit mode and have permission to edit this scratchpad
    inEditModeAndCanEdit?: boolean;
    canEdit: boolean;
    isDeveloper: boolean;
    isOwner: boolean;
    isModerator: boolean;
    profileRoot?: string;
    useContentHeader: boolean;
    userAuthoredContentType: string;
    // Set to true if scratchpad.get("byChild") is true
    private: boolean;
    translatedTitle: string;
    translatedDescription: string | null | undefined;
    // Is this a "New program" page
    isNew?: boolean;
    // Is this a project?
    isProject: boolean;
    isDocumentation: boolean;
    isOfficial: boolean;
    isChallenge: boolean;
    isUneditableProjectFork: boolean;
    // A promise that will resolve when we have the ScratchpadRecord
    liveEditorPromise: Promise<LiveEditor>;
    // Listen for when updates to the play state occur (such as
    // the current playback time changing)
    registerPlayUpdate: (arg1: (timeInSeconds: number) => unknown) => unknown;
    // The scratchapd's title
    newTitle: string;
    // The origin scratchpad Backbone model
    originScratchpad: typeof Scratchpad | null | undefined;
    // The scratchpad Backbone model which is being rendered
    scratchpad: typeof Scratchpad;
    // The UserScratchpad in use on the project or challenge
    userScratchpad: typeof UserScratchpad;
    // Should the project help button be shown
    showProjectHelpButton: boolean;
    // Should the start over button be shown
    showStartOverButton: boolean;
    // Should the project eval area be shown
    showProjectEvalArea: boolean;
    // Should the share button be shown
    showShareButton?: boolean;
    // Topic used for to do proper styling
    topic: ScratchpadTopic;
    // The slug for the topic in which this scratchpad is being rendered
    topicSlug: string;
    isComputerProgramming: boolean;
    // The last time this scratchpad was updated
    lastUpdatedTime: string;
    // Handle when properties are toggled (such as for the mod controls)
    onToggleProgramProp: (
        gqlFetch: GqlFetchFn,
        property: "deleted" | "hideFromHotlist",
    ) => Promise<void>;
    onTriggerSpinoff: () => unknown;
    // Fires during a seek
    onSeek: (timeInSeconds: number) => unknown;
    // When the seeking ends
    onSeekEnd: () => unknown;
    // When the seeking begins
    onSeekStart: () => unknown;
    // Handle when the "Start over" button is clicked
    onStartOver: () => unknown;
    // Handle when the "Undo" button is clicked
    onUndo: () => unknown;
    // The label to use for the restart button
    restartLabel: string;
    // A place to register a callback for external triggering of
    // the save flow on a scratchpad
    registerTriggerSave: (
        callback: (saveSettings: boolean) => unknown,
    ) => unknown;
    // A place to register a callback for external triggering of
    // the spin-off flow on a scratchpad
    registerTriggerSpinOff: (callback: () => unknown) => unknown;
    // Handle saving the current Program (previously known as a Scratchpad)
    saveProgram: SaveProgramFn;
    // Set the title using a promise
    setTitle: (title: string) => Promise<unknown>;
    // DeleteScratchpadButton calls this function to delete a scratchpad
    deleteProgram: (gqlFetch: GqlFetchFn) => Promise<void>;
    // Callback to call when saving changes
    onSave: (saveSettings: boolean) => unknown;
    // A set of properties to add to the Sandbox URL
    sandboxProps: string;
    // The URL in which all the code is being run
    sandboxUrl: string;
    // A URL for a Youtube replacement for the talkthrough
    youtubeUrl: string;
    // Callback to determine if the scratchpaad has unsaved changes
    hasUnsavedChanges: () => boolean;
    // A way to register a callback that will fire when the code runs
    registerCodeRun: (callback: () => unknown) => unknown;
    // A way to register a callback that will fire when the code changes
    registerCodeChange: (callback: () => unknown) => unknown;
    // A way to register a callback that will fire when user changes the code
    registerUserCodeChange: (callback: () => unknown) => unknown;
    // A function that determines whether the current code should
    // cause the restart button to be hidden
    shouldHideRestart: () => boolean;
    discussionMeta?: DiscussionMeta;
    // Undefined for new programs, talkthroughs, challenges, interactives
    scratchpadKey: string | null | undefined;
};

type Props = OwnProps & WithGqlProps & WithMarkConversionProps;

type Transcript = any;

type State = {
    loadingTranscript: boolean;
    numProjectSpinoffs: number;
    transcript: Transcript | null | undefined;
};

class ScratchpadView extends React.Component<Props, State> {
    // TODO(briangenisio): This is a hack to remove the deprecated call to
    // this.isMounted() but is still considered an anti-pattern.  Replace
    // with code which does not rely on the mounted state like Redux or GraphQL
    _mounted: boolean;
    discussionNode: StatefulDiscussionType | null | undefined;
    DiscussionProjectFeedback: DiscussionProjectFeedbackT | null | undefined;

    constructor(props: Props) {
        super(props);
        this.state = {
            loadingTranscript: false,
            numProjectSpinoffs: -1,
            transcript: null,
        };
        this.discussionNode = null;
        this._mounted = false;
    }

    componentDidMount() {
        this.loadTranscript();
        this.lazyLoadProjectFeedback();
        this.lazyLoadUserSpinoffs();

        this._mounted = true;

        this.markViewConversion();
    }

    componentWillUnmount() {
        this._mounted = false;

        // DiscussionProjectFeedback isn't a React component, but it renders
        // a component to a separate render root.  We have to manually tell it
        // to unmount.
        if (this.DiscussionProjectFeedback) {
            this.DiscussionProjectFeedback.unmount();
        }
    }

    markViewConversion() {
        // We only mark conversions for non-embedded user-created programs,
        // since tutorial-page marks conversions for in-course content.
        if (
            // True for talkthroughs/challenges/projects/interactives
            (!this.props.isNew &&
                this.props.scratchpad.get("contentKind") !== "Scratchpad") ||
            // True when program is embedded inside article or non-KA page
            this.props.embeddedAndWithFrame
        ) {
            return;
        }
        let conversionId;
        if (this.props.isNew) {
            conversionId = "pageview_newprogram";
        } else if (this.props.isOwner) {
            conversionId = "pageview_myprogram";
        } else {
            conversionId = "pageview_otherprogram";
        }
        this.props.markConversion({
            id: conversionId,
        });
    }

    lazyLoadProjectFeedback() {
        Promise.all([
            import("../discussion-package/discussion-projectfeedback.tsx").then(
                (module) => module.default,
            ),
        ]).then(([DiscussionProjectFeedback]: [any]) => {
            if (!this._mounted) {
                return;
            }

            const {scratchpad, discussion} = this.props;

            // If the user is on a program that they own
            if (
                scratchpad.isOwner() ||
                scratchpad.isProject() ||
                !scratchpad.inTutorial()
            ) {
                this.DiscussionProjectFeedback = DiscussionProjectFeedback;
                DiscussionProjectFeedback.load(discussion, () => {
                    if (
                        !DiscussionProjectFeedback.shouldShowTab ||
                        DiscussionProjectFeedback.shouldShowTab()
                    ) {
                        $(DiscussionProjectFeedback._tabHeaderSelector).show();
                    }
                });
            }
        });
    }

    lazyLoadUserSpinoffs() {
        const {scratchpad, gqlFetch} = this.props;

        // If its a project thats complete, then we show their completed
        //  projects in the spin-offs tab and link to that from the top.
        // If its a project that's not complete, then we actually serve
        //  the user their spin-off directly, so they don't need any
        //  direction to their spin-offs.
        if (scratchpad.isProject()) {
            ScratchpadUtils.loadOneUserSpinoff(scratchpad.id, gqlFetch).then(
                (scratchpads) => {
                    if (this._mounted) {
                        this.setState({numProjectSpinoffs: scratchpads.length});
                    }
                },
            );
        }
    }

    renderHeader: () => React.ReactNode = () => {
        if (this.props.embeddedAndWithFrame) {
            return null;
        }

        const {
            challenge,
            embedded,
            isChallenge,
            isDocumentation,
            isOfficial,
            isOwner,
            isUneditableProjectFork,
            onTriggerSpinoff,
            originScratchpad,
            profileRoot,
            scratchpad,
            showDescription,
            topic,
            userScratchpad,
            inEditModeAndCanEdit,
            isSaveable,
            onSave,
            setTitle,
            translatedTitle,
        } = this.props;

        return (
            <ScratchpadHeader
                challenge={challenge}
                embedded={embedded}
                isChallenge={isChallenge}
                isDocumentation={isDocumentation}
                isOfficial={isOfficial}
                isOwner={isOwner}
                isUneditableProjectFork={isUneditableProjectFork}
                onTriggerSpinoff={onTriggerSpinoff}
                originScratchpad={originScratchpad}
                profileRoot={profileRoot}
                scratchpad={scratchpad}
                showDescription={showDescription}
                topic={topic}
                userScratchpad={userScratchpad}
                inEditModeAndCanEdit={inEditModeAndCanEdit}
                isSaveable={isSaveable}
                onSave={onSave}
                setTitle={setTitle}
                translatedTitle={translatedTitle}
            />
        );
    };

    renderContent(): React.ReactNode {
        const {
            appVersion,
            creatorProfile,
            embedded,
            embeddedAndWithFrame,
            hasAudio,
            hideEditor,
            inEditModeAndCanEdit,
            lastUpdatedTime,
            originScratchpad,
            sandboxProps,
            sandboxUrl,
            scratchpad,
            showProjectEvalArea,
            userScratchpad,
            youtubeUrl,
            challenge,
            hideSave,
            isChallenge,
            isNew,
            isProject,
            isSaveable,
            isSpinOffAllowed,
            liveEditorPromise,
            onSeek,
            onSeekEnd,
            onSeekStart,
            onStartOver,
            onUndo,
            registerTriggerSave,
            registerTriggerSpinOff,
            restartLabel,
            saveProgram,
            setTitle,
            showProjectHelpButton,
            showStartOverButton,
            topic,
            shouldHideRestart,
            registerCodeRun,
            registerCodeChange,
            registerUserCodeChange,
        } = this.props;

        return (
            <ScratchpadContent
                appVersion={appVersion}
                creatorProfile={creatorProfile}
                embedded={embedded}
                embeddedAndWithFrame={embeddedAndWithFrame}
                hasAudio={hasAudio}
                hideEditor={hideEditor}
                inEditModeAndCanEdit={inEditModeAndCanEdit}
                lastUpdatedTime={lastUpdatedTime}
                originScratchpad={originScratchpad}
                sandboxProps={sandboxProps}
                sandboxUrl={sandboxUrl}
                scratchpad={scratchpad}
                showProjectEvalArea={showProjectEvalArea}
                userScratchpad={userScratchpad}
                youtubeUrl={youtubeUrl}
                numProjectSpinoffs={this.state.numProjectSpinoffs}
                onRequestHelp={this.onRequestHelp}
                challenge={challenge}
                hideSave={hideSave}
                isChallenge={isChallenge}
                isNew={isNew}
                isProject={isProject}
                isSaveable={isSaveable}
                isSpinOffAllowed={isSpinOffAllowed}
                liveEditorPromise={liveEditorPromise}
                onSeek={onSeek}
                onSeekEnd={onSeekEnd}
                onSeekStart={onSeekStart}
                onStartOver={onStartOver}
                onUndo={onUndo}
                registerTriggerSave={registerTriggerSave}
                registerTriggerSpinOff={registerTriggerSpinOff}
                restartLabel={restartLabel}
                saveProgram={saveProgram}
                setTitle={setTitle}
                showProjectHelpButton={showProjectHelpButton}
                showStartOverButton={showStartOverButton}
                topic={topic}
                shouldHideRestart={shouldHideRestart}
                registerCodeRun={registerCodeRun}
                registerCodeChange={registerCodeChange}
                registerUserCodeChange={registerUserCodeChange}
            />
        );
    }

    onRequestHelp: () => void = () => {
        const duration = standardTransitionMs * 2;
        // We use a setTimeout with 0 ms to make sure this method call happens
        // after the discussionRoot element is actually ready to be focused on.
        // TODO(jeff, LP-6954): Use Wonder Blocks Timing API
        // eslint-disable-next-line no-restricted-syntax
        setTimeout(() => {
            // We wait until now to call findDOMNode because the ScratchpadView
            // instance that is initially rendered by scratchpad-ui.js gets re-rendered
            // by ScratchpadPage.  If we call findDOMNode in the ref callback we end
            // with a stale element that isn't attaced to the DOM.
            const discussionRoot = findDOMNode(this.discussionNode) as any;
            if (discussionRoot) {
                scrollToElement(discussionRoot, duration, () => {
                    if (discussionRoot) {
                        discussionRoot.focus();
                    }
                });
            }
        }, 0);
    };

    renderDiscussion(): React.ReactNode {
        const {topicSlug, discussion, hideDiscussion, scratchpad} = this.props;

        // Discussions are hidden in several cases, especially if the content is a
        // scratchpad created by a user under the age of 13. This is related to
        // privacy regulations in which we need to remove the possibility of other
        // users communicating with a user under the age of 13.
        // We can't use showProjectFeedback here because that will remove discussions
        // from official scratchpads too, which includes contest scratchpads.
        if (hideDiscussion || !discussion || scratchpad.attributes.byChild) {
            return null;
        }

        const {
            expandFeedbackType,
            qaExpandKey,
            restrictPosting,
            showProjectFeedback,
        } = discussion;

        // All properties on Backbone Model `attributes` are mixed
        const authorKaid: string = scratchpad.attributes.kaid as any;

        // Re-create a discussion context that makes Discussion happy
        const discussionContext = {
            expandFeedbackType,
            qaExpandKey,
            restrictPosting,
            showProjectFeedback,
            authorKaid,
        } as const;

        return (
            <Discussion
                ref={(node) => (this.discussionNode = node)}
                contentKind={discussion.focusKind}
                contentId={discussion.focusId}
                topicSlug={topicSlug}
                discussionContext={discussionContext}
                onNextPage={() => {}}
            />
        );
    }

    // iPad Navigation
    renderiPadNavigation(): React.ReactNode {
        return <div className="ipad-scratchpad-wrap-outer" />;
    }

    renderLicense(): React.ReactNode {
        const mitUrl = "http://opensource.org/licenses/mit-license.php";
        const ccUrl = "https://creativecommons.org/licenses/by/4.0/";
        const tosUrl = "/about/docs/khan-academy-terms-of-service#user-content";

        return (
            <div className="license">
                <ul>
                    <li>
                        {t`All code is owned by its respective author and made available under the following license:`}{" "}
                        <a href={mitUrl}>{t`MIT license`}</a>
                    </li>
                    <li>
                        {t`All non-code (such as writing, drawings, images, etc.) are also owned by their respective author and made available under the following license:`}{" "}
                        <a href={ccUrl}>
                            {t`Creative Commons Attribution License`}
                        </a>
                    </li>
                    <li>
                        {t`For more information please see:`}{" "}
                        <a href={tosUrl}>{t`Terms of Service`}</a>
                    </li>
                </ul>
            </div>
        );
    }

    loadTranscript() {
        const youtubeId = this.props.scratchpad.getYoutubeId();

        if (
            !youtubeId ||
            this.state.transcript ||
            this.state.loadingTranscript
        ) {
            return;
        }

        this.setState({loadingTranscript: true});

        ScratchpadUtils.loadTranscript(youtubeId).then((transcript) => {
            if (this._mounted) {
                this.setState({transcript});
            }
        });
    }

    renderTabs(): React.ReactNode {
        const {creatorProfile, scratchpad} = this.props;
        const tabs: Array<Tab> = [];
        const contentType = this.props.scratchpad.get(
            "userAuthoredContentType",
        );

        if (creatorProfile || scratchpad.get("translatedDescription")) {
            const {
                embedded,
                hasUnsavedChanges,
                lastUpdatedTime,
                newTitle,
                onSave,
                onToggleProgramProp,
                originScratchpad,
                scratchpadKey,
                canEdit,
                discussionMeta,
                inEditModeAndCanEdit,
                isNew,
                isModerator,
                isDocumentation,
                isOwner,
                isOfficial,
                hideSpinOffs,
                deleteProgram,
            } = this.props;
            tabs.push({
                title: t`About`,
                content: (
                    <ScratchpadAbout
                        creatorProfile={creatorProfile}
                        embedded={embedded}
                        hasUnsavedChanges={hasUnsavedChanges}
                        lastUpdatedTime={lastUpdatedTime}
                        newTitle={newTitle}
                        onSave={onSave}
                        onToggleProgramProp={onToggleProgramProp}
                        originScratchpad={originScratchpad}
                        scratchpad={scratchpad}
                        scratchpadKey={scratchpadKey}
                        canEdit={canEdit}
                        discussionMeta={discussionMeta}
                        inEditModeAndCanEdit={inEditModeAndCanEdit}
                        isNew={isNew}
                        isModerator={isModerator}
                        isDocumentation={isDocumentation}
                        isOwner={isOwner}
                        isOfficial={isOfficial}
                        hideSpinOffs={hideSpinOffs}
                        deleteProgram={deleteProgram}
                        private={this.props.private}
                        showShareButton={this.props.showShareButton}
                    />
                ),
            });
        }

        if (!this.props.hideDocumentation) {
            const inCourse = !this.props.isNew && this.props.embedded;

            tabs.push({
                title: t`Documentation`,
                content: (
                    <ProgrammingDocs
                        type={contentType}
                        hideTitle={false}
                        moduleLayout={false}
                        inCourse={inCourse}
                    />
                ),
            });
        }

        if (!this.props.hideSpinOffs && this.props.scratchpad.id) {
            tabs.push({
                title: t`Spin-offs`,
                content: (
                    <ScratchpadSpinOffs
                        scratchpadId={this.props.scratchpad.id.toString()}
                        scratchpadKind={this.props.scratchpad.get(
                            "contentKind",
                        )}
                    />
                ),
            });
        }

        if (this.state.transcript) {
            const {onSeek, onSeekEnd, onSeekStart, registerPlayUpdate} =
                this.props;

            tabs.push({
                title: t`Transcript`,
                content: (
                    <ScratchpadTranscript
                        onSeek={onSeek}
                        onSeekEnd={onSeekEnd}
                        onSeekStart={onSeekStart}
                        registerPlayUpdate={registerPlayUpdate}
                        transcript={this.state.transcript}
                    />
                ),
            });
        }

        if (
            // Don't show for Talkthrough/Project/Challenge/Interactive
            this.props.isNew ||
            (this.props.scratchpad.get("contentKind") === "Scratchpad" &&
                !this.props.scratchpad.get("isPublished"))
        ) {
            tabs.push({
                title: t`Guidelines`,
                content: (
                    <ScratchpadGuidelines
                        userAuthoredContentType={contentType}
                    />
                ),
            });
        }

        return <ScratchpadTabs tabs={tabs} />;
    }

    renderFooter(): React.ReactNode {
        if (this.props.embeddedAndWithFrame) {
            return null;
        }

        return (
            <div className={css(styles.footer)}>
                {this.renderTabs()}
                {this.renderDiscussion()}
                {this.renderiPadNavigation()}
                {this.renderLicense()}
            </div>
        );
    }

    render(): React.ReactNode {
        return (
            <div>
                {this.renderHeader()}
                <div className={classNames("scratchpad-wrap-outer")}>
                    {this.renderContent()}
                    {this.renderFooter()}
                </div>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    footer: {
        margin: "0 auto",
        maxWidth: 688,
    },
});

const mapPropsToLearningTime = (
    ownProps: OwnProps,
): PartialLearningTimeInfo => {
    return {
        activity: enums.Activity.OTHER_ACTIVITY,
        product: enums.Product.OTHER_PRODUCT,
    };
};

type ExportProps = WithoutGql<
    WithoutMarkConversion<
        JSX.LibraryManagedAttributes<
            typeof ScratchpadView,
            React.ComponentProps<typeof ScratchpadView>
        >
    >
>;

export default compose(
    withGql,
    withConversionContext(mapPropsToLearningTime),
    withMarkConversion,
)(ScratchpadView) as React.ComponentType<ExportProps>;
